import React from "react";
import {
  Box,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";
import { useDashboard } from "../hooks/useDashboard";

const StatCard = ({ title, value, color = "white" }) => {
  return (
    <Card
      bg="#121212"
      borderColor="#3A3A3A"
      borderWidth="1px"
      borderRadius="lg"
    >
      <CardBody p="6">
        <Stat>
          <StatLabel color="gray.400" fontSize="sm" fontWeight="medium" mb="2">
            {title}
          </StatLabel>
          <StatNumber
            fontSize="3xl"
            fontWeight="bold"
            color={color}
            lineHeight="1"
          >
            {value}
          </StatNumber>
        </Stat>
      </CardBody>
    </Card>
  );
};

const Dashboard = () => {
  const { dashboardData, loading, error } = useDashboard();

  const userRole = localStorage.getItem("user_role");

  // Show loading spinner
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <Spinner size="xl" color="orange.500" />
      </Box>
    );
  }

  // Show error message
  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>
          Failed to load dashboard data: {error}
        </AlertDescription>
      </Alert>
    );
  }

  // Default stats if no data is available
  const getDefaultStats = () => {
    if (userRole === "Admin") {
      return [
        { title: "Total Tables", value: "0", color: "white" },
        { title: "Total Searches", value: "0", color: "green.400" },
        { title: "Total Models", value: "0", color: "purple.400" },
        { title: "Total Users", value: "0", color: "orange.400" },
      ];
    } else {
      return [
        { title: "Assigned Tasks", value: "0", color: "cyan.400" },
        { title: "Completed Tasks", value: "0", color: "green.400" },
        { title: "Total Models", value: "0", color: "yellow.400" },
        { title: "Progress", value: "0%", color: "pink.400" },
      ];
    }
  };

  // Extract stats from API response or use defaults
  const getDashboardStats = () => {
    if (dashboardData && Object.keys(dashboardData).length > 0) {
      if (userRole === "Admin") {
        return [
          {
            title: "Total Batches",
            value: dashboardData.total_batches || 0,
            color: "blue.400",
          },
          {
            title: "Total Models",
            value: dashboardData.total_models || 0,
            color: "green.400",
          },
          {
            title: "Total Tasks",
            value: dashboardData.total_tasks || 0,
            color: "purple.400",
          },
          {
            title: "Total Users",
            value: dashboardData.total_users || 0,
            color: "orange.400",
          },
        ];
      } else {
        // For annotators/reviewers, adapt the data or use different fields
        return [
          {
            title: "Completed Tasks",
            value: dashboardData.completed_tasks || 0,
            color: "purple.400",
          },
          {
            title: "Pending Tasks",
            value: dashboardData.pending_tasks || 0,
            color: "cyan.400",
          },
          {
            title: "Total Assigned Models",
            value: dashboardData.total_assigned_models || 0,
            color: "green.400",
          },
          {
            title: "Processed Models",
            value: dashboardData.Processed_Models || 0,
            color: "yellow.400",
          },
        ];
      }
    }
    return getDefaultStats();
  };

  const dashboardStats = getDashboardStats();

  return (
    <Box>
      {/* Header */}
      <Box mb="8">
        <Text fontSize="3xl" fontWeight="bold" color="white" mb="2">
          Dashboard Overview
        </Text>
        <Text color="gray.400" fontSize="md">
          {userRole === "Admin"
            ? "Monitor your 3D model processing pipeline at a glance"
            : "Track your task progress and performance"}
        </Text>
      </Box>

      {/* Stats Grid */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing="6">
        {dashboardStats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </SimpleGrid>

      {/* Additional content area */}
      <Box mt="12">
        {/* Future dashboard content can be added here */}
        <Box
          bg="#121212"
          borderRadius="lg"
          p="6"
          borderColor="#3A3A3A"
          borderWidth="1px"
          minH="200px"
        >
          <Text color="gray.400" fontSize="sm">
            {userRole === "Admin"
              ? "Additional admin dashboard content will be displayed here"
              : "Additional user dashboard content will be displayed here"}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
export default Dashboard;
